/**
 * Notification preferences: per user and network, which notifications reach
 * them and how. A subscription covers every notification about one object
 * type from a severity up; an override sends one specific notification by the
 * chosen methods regardless of the subscriptions.
 */

import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'
import type { NotificationSeverity } from './notificationsApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

export const DELIVERY_METHODS = ['EMAIL', 'UI'] as const
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number]

/** The object types a user can subscribe to, as the old UI offers them. */
export const SUBSCRIPTION_TYPES = ['LINE_ITEM', 'CREATIVE', 'REPORT_EXECUTION', 'DATA_EXPORT'] as const
export type SubscriptionType = (typeof SUBSCRIPTION_TYPES)[number]

/** The notifications that can be overridden one by one, with the severity each is sent at. */
export const OVERRIDABLE_MESSAGES: { id: string; severity: NotificationSeverity }[] = [
  { id: 'LINE_ITEM_READY', severity: 'INFO' },
  { id: 'LINE_ITEM_RUNNING', severity: 'IMPORTANT' },
  { id: 'LINE_ITEM_STOPPED_RUNNING', severity: 'IMPORTANT' },
  { id: 'LINE_ITEM_ENDED_END_DATE_PASSED', severity: 'IMPORTANT' },
  { id: 'LINE_ITEM_ENDED_OBJECTIVE_REACHED', severity: 'IMPORTANT' },
  { id: 'LINE_ITEM_NOT_STARTED', severity: 'WARNING' },
  { id: 'LINE_ITEM_DELETED', severity: 'INFO' },
  { id: 'LINE_ITEM_SUBMITTED', severity: 'IMPORTANT' },
  { id: 'LINE_ITEM_REJECTED', severity: 'IMPORTANT' },
  { id: 'CREATIVE_REJECTED', severity: 'IMPORTANT' },
  { id: 'REPORT_COMPLETE', severity: 'INFO' },
  { id: 'DATA_EXPORT_HAS_FAILED', severity: 'ERROR' },
]

export interface NotificationSubscription {
  severityLevel: NotificationSeverity
  deliveryMethods: DeliveryMethod[]
}

export interface NotificationPreferences {
  id: string
  subscriptions: Partial<Record<SubscriptionType, NotificationSubscription>>
  overrides: Record<string, DeliveryMethod[]>
}

export const NotificationPreferencesApi = {
  /**
   * The user's preferences on the current network. A user who has never saved
   * any gets a fresh, empty set under a new id, ready to be saved.
   */
  get: async (userId: string): Promise<NotificationPreferences> => {
    const params = new URLSearchParams({ user: userId, context: getContext() })
    const res = await apiFetch(`${API_BASE}/notificationpreferences?${params}`)
    if (!res.ok) await throwApiError(res, 'Failed to load notification preferences')
    // No preferences yet answers with an empty body.
    const text = await res.text()
    const data = text ? (JSON.parse(text) as Partial<NotificationPreferences>) : null
    return {
      id: data?.id || crypto.randomUUID().replace(/-/g, '').slice(0, 16),
      subscriptions: data?.subscriptions ?? {},
      overrides: data?.overrides ?? {},
    }
  },

  /**
   * Saves the whole set. Every subscription sent needs a severity, so leave
   * out the types the user is not subscribed to.
   */
  save: async (userId: string, prefs: NotificationPreferences): Promise<void> => {
    const res = await apiFetch(
      `${API_BASE}/notificationpreferences/${encodeURIComponent(prefs.id)}?context=${getContext()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...prefs, user: { id: userId } }),
      }
    )
    if (!res.ok) await throwApiError(res, 'Failed to save notification preferences')
  },
}
